import { useEffect } from 'react';
import { type Control, FieldPath, FieldValues, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { CheckboxField, TextInputField } from '@brighte/form-components';
import { AddressType, isBusinessLoanProduct } from '@brighte/validation';
import { Address } from '@components/Address';
import { Text } from '@spark-web/text';
import { useApplicationData } from '../../provider';
import { LABELS, OWNERSHIP_DETAILS } from '../../utils';
import SmeDriverLicenceFields from './SmeDriverLicenceFields';

type SoleTraderIndividualProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  defaultValues?: { address?: AddressType | null };
};

function SoleTraderIndividual<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  watch,
  setValue,
  defaultValues,
}: Readonly<SoleTraderIndividualProps<TFieldValues>>) {
  const { FIRST_NAME, MIDDLE_NAME, LAST_NAME, HOME_ADDRESS, EMAIL_ADDRESS, PHONE_NUMBER } = LABELS;
  const { DESCRIPTION_COMPANY } = OWNERSHIP_DETAILS;

  const { BusinessDetails: businessDetails, ContactDetails: contactDetails, quote } = useApplicationData([
    'BusinessDetails',
    'ContactDetails',
    'quote',
  ]);
  const isBusinessProduct = isBusinessLoanProduct(quote?.financialProduct?.id);


  useEffect(() => {
    const firstName = businessDetails?.applicant?.firstName;
    const lastName = businessDetails?.applicant?.lastName;
    const email = contactDetails?.email;
    const phone = contactDetails?.mobile;

    if (firstName) setValue?.(`${name}.firstName` as FieldPath<TFieldValues>, firstName as any, { shouldDirty: true });
    if (lastName) setValue?.(`${name}.lastName` as FieldPath<TFieldValues>, lastName as any, { shouldDirty: true });
    if (email) setValue?.(`${name}.email` as FieldPath<TFieldValues>, email as any, { shouldDirty: true });
    if (phone) setValue?.(`${name}.phone` as FieldPath<TFieldValues>, phone as any, { shouldDirty: true });
  }, [businessDetails, contactDetails, name, setValue]);

  return (
    <>
      <Text>{DESCRIPTION_COMPANY}</Text>

      <TextInputField<TFieldValues>
        name={`${name}.firstName` as FieldPath<TFieldValues>}
        label={FIRST_NAME}
        control={control}
        maxLength={255}
      />
      <TextInputField<TFieldValues>
        name={`${name}.middleName` as FieldPath<TFieldValues>}
        label={MIDDLE_NAME}
        control={control}
        maxLength={255}
        FieldProps={{ secondaryLabel: '(Optional)' }}
      />
      <TextInputField<TFieldValues>
        name={`${name}.lastName` as FieldPath<TFieldValues>}
        label={LAST_NAME}
        control={control}
        maxLength={255}
      />

      <TextInputField<TFieldValues>
        name={`${name}.email` as FieldPath<TFieldValues>}
        label={EMAIL_ADDRESS}
        control={control}
        maxLength={255}
      />
      <TextInputField<TFieldValues>
        name={`${name}.phone` as FieldPath<TFieldValues>}
        label={PHONE_NUMBER}
        control={control}
      />

      <SmeDriverLicenceFields name={name} control={control} watch={watch} />

      <Address
        control={control}
        label={HOME_ADDRESS}
        name={`${name}.address` as FieldPath<TFieldValues>}
        setValue={setValue}
        defaultValue={defaultValues?.address ?? null}
      />

      {isBusinessProduct && (
        <CheckboxField
          name={`${name}.privacyAndCreditStatement` as FieldPath<TFieldValues>}
          control={control}
          checkedValue={OWNERSHIP_DETAILS.PRIVACY_AND_CREDIT_TEXT}
        >
          {OWNERSHIP_DETAILS.PRIVACY_AND_CREDIT_STATEMENT}
        </CheckboxField>
      )}
    </>
  );
}

export default SoleTraderIndividual;
